import { useState, useEffect, ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { User, Session } from '@supabase/supabase-js';
import { Loader2 } from 'lucide-react';
import { portalPath } from '@/shared/app/portalPath';

interface ProtectedAgentRouteProps {
  children: ReactNode;
}

const ProtectedAgentRoute = ({ children }: ProtectedAgentRouteProps) => {
  const p = portalPath('agent');
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isAgent, setIsAgent] = useState<boolean>(false);
  const [agentStatus, setAgentStatus] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const checkAgentRole = async (userId: string, email?: string | null) => {
      try {
        // Check if user has 'agent' OR 'agent_master' role
        const [agentResult, masterResult] = await Promise.all([
          supabase.rpc('has_role', { _user_id: userId, _role: 'agent' }),
          supabase.rpc('has_role', { _user_id: userId, _role: 'agent_master' }),
        ]);

        if (!isMounted) return;

        const hasAgentRole = !agentResult.error && agentResult.data === true;
        const hasMasterRole = !masterResult.error && masterResult.data === true;
        
        if (!hasAgentRole && !hasMasterRole) {
          setIsAgent(false);
          return;
        }

        // Check agent_profiles for active status (optional for master-by-role/email).
        const { data: profileData, error: profileError } = await supabase
          .from('agent_profiles')
          .select('agent_code, is_active')
          .eq('user_id', userId)
          .maybeSingle();

        if (!isMounted) return;

        if (profileError) {
          console.warn('[AgentGuard] agent_profiles lookup error:', profileError.message);
        }

        if (profileData) {
          setIsAgent(true);
          setAgentStatus(profileData.is_active ? 'active' : 'inactive');
        } else if (hasMasterRole) {
          // Master users may not have an agent_profiles row — allow them in.
          console.warn('[AgentGuard] No agent_profiles row for master user; allowing access via role/email.');
          setIsAgent(true);
          setAgentStatus('active');
        } else {
          setIsAgent(false);
        }
      } catch (error) {
        console.error('Error checking agent role:', error);
        if (isMounted) setIsAgent(false);
      }
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, currentSession) => {
        if (!isMounted) return;
        
        setSession(currentSession);
        setUser(currentSession?.user ?? null);
        
        if (currentSession?.user) {
          setTimeout(() => {
            checkAgentRole(currentSession.user.id, currentSession.user.email);
          }, 0);
        } else {
          setIsAgent(false);
          setAgentStatus(null);
        }
      }
    );

    const initializeAuth = async () => {
      try {
        const { data: { session: initialSession } } = await supabase.auth.getSession();
        
        if (!isMounted) return;

        setSession(initialSession);
        setUser(initialSession?.user ?? null);
        
        if (initialSession?.user) {
          await checkAgentRole(initialSession.user.id, initialSession.user.email);
        }
      } catch (error) {
        console.error('Error during initial auth setup:', error);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    initializeAuth();

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
          <p className="mt-4 text-muted-foreground">Verifying agent access...</p>
        </div>
      </div>
    );
  }

  // Not authenticated
  if (!session || !user) {
    return <Navigate to={p('/login')} replace />;
  }

  // Not an agent
  if (!isAgent) {
    return <Navigate to={p('/login')} replace />;
  }

  // Agent is inactive
  if (agentStatus !== 'active') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center max-w-md p-8">
          <div className="w-16 h-16 mx-auto mb-4 bg-destructive/10 rounded-full flex items-center justify-center">
            <Loader2 className="h-8 w-8 text-destructive" />
          </div>
          <h1 className="text-xl font-semibold text-foreground mb-2">Account Inactive</h1>
          <p className="text-muted-foreground">
            Your account is inactive. Please contact admin.
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default ProtectedAgentRoute;
